// src/components/auth/ResetPassword.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Logo } from '../../hooks/useLogo';

export const ResetPassword = () => {
    usePageTitle('Reset Password');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Invalid reset link. Token is missing.');
        }
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setStatus('loading');

        try {
            const response = await authService.resetPassword(token, formData.password);
            setStatus('success');
            setMessage(response.message);

            // Auto redirect after 5 seconds
            setTimeout(() => {
                navigate('/login');
            }, 5000);
        } catch (error) {
            setStatus('error');
            setMessage(error.message || 'Password reset failed. Please try again.');
        }
    };

    if (status === 'success') {
        return (
            <div className="reset-password-page">
                <div className="reset-password-container">
                    <div className="reset-password-content">
                        <div className="success-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <h2>Password Reset Successfully!</h2>
                        <p>{message}</p>
                        <p>You can now log in with your new password.</p>

                        <div className="redirect-notice">
                            <p>You'll be redirected to login in a few seconds...</p>
                        </div>

                        <Link to="/login" className="btn btn-primary">
                            <i className="fas fa-sign-in-alt"></i>
                            Go to Login Now
                        </Link>
                    </div>
                </div>
                {renderStyles()}
            </div>
        );
    }

    if (status === 'error' && message.includes('Invalid reset link')) {
        return (
            <div className="reset-password-page">
                <div className="reset-password-container">
                    <div className="reset-password-content">
                        <div className="error-icon">
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <h2>Reset Link Invalid</h2>
                        <p>{message}</p>

                        <div className="error-actions">
                            <Link to="/forgot-password" className="btn btn-secondary">
                                <i className="fas fa-key"></i>
                                Request New Reset
                            </Link>
                            <Link to="/login" className="btn btn-primary">
                                <i className="fas fa-sign-in-alt"></i>
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </div>
                {renderStyles()}
            </div>
        );
    }

    return (
        <div className="reset-password-page">
            <div className="reset-password-container">
                <div className="reset-password-content">
                    <Logo size="medium" usePublicLogo={true} />

                    <div className="reset-icon">
                        <i className="fas fa-lock"></i>
                    </div>

                    <h2>Set New Password</h2>
                    <p>Enter your new password below. Make sure it's strong and secure.</p>

                    <form onSubmit={handleSubmit}>
                        {status === 'error' && !message.includes('Invalid reset link') && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-circle"></i>
                                {message}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="password">New Password</label>
                            <div className="password-input-container">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={errors.password ? 'form-input error' : 'form-input'}
                                    placeholder="Enter new password"
                                    required
                                />
                                <i className="fas fa-lock input-icon"></i>
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                                </button>
                            </div>
                            {errors.password && (
                                <span className="form-error">{errors.password}</span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className="password-input-container">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className={errors.confirmPassword ? 'form-input error' : 'form-input'}
                                    placeholder="Confirm new password"
                                    required
                                />
                                <i className="fas fa-lock input-icon"></i>
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <span className="form-error">{errors.confirmPassword}</span>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                            disabled={status === 'loading'}
                        >
                            {status === 'loading' ? (
                                <>
                                    <div className="spinner"></div>
                                    Resetting Password...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save"></i>
                                    Reset Password
                                </>
                            )}
                        </button>
                    </form>

                    <div className="back-to-login">
                        <Link to="/login">
                            <i className="fas fa-arrow-left"></i>
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
            {renderStyles()}
        </div>
    );
};

// Shared styles function
const renderStyles = () => (
    <style>{`
        .reset-password-page {
            min-height: 100vh;
            height: 100%;
            background: var(--gradient);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2rem;
            flex: 1;
        }

        .reset-password-container {
            max-width: 400px;
            width: 100%;
            background: var(--white);
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
        }

        .reset-password-content {
            padding: 3rem;
            text-align: center;
        }

        .logo {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 2rem;
            justify-content: center;
        }

        .logo-icon {
            width: 40px;
            height: 40px;
            background: var(--gradient);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
        }

        .logo-text {
            font-size: 1.5rem;
            font-weight: bold;
            background: var(--gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }

        .reset-icon {
            width: 60px;
            height: 60px;
            background: var(--gradient);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.5rem;
            font-size: 1.5rem;
            color: white;
        }

        .success-icon {
            width: 80px;
            height: 80px;
            background: #e8f5e8;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 2rem;
            font-size: 2.5rem;
            color: #2e7d32;
        }

        .error-icon {
            width: 80px;
            height: 80px;
            background: #ffebee;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 2rem;
            font-size: 2.5rem;
            color: #d32f2f;
        }

        h2 {
            color: var(--text-dark);
            margin-bottom: 1rem;
            font-size: 1.8rem;
        }

        p {
            color: var(--text-light);
            line-height: 1.6;
            margin-bottom: 1.5rem;
        }

        .redirect-notice {
            background: #e3f2fd;
            padding: 1rem;
            border-radius: 8px;
            margin: 1.5rem 0;
            border: 1px solid #bbdefb;
        }

        .redirect-notice p {
            margin: 0;
            color: #1565c0;
            font-size: 14px;
            font-weight: 500;
        }

        .form-group {
            margin-bottom: 1.5rem;
            position: relative;
            text-align: left;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            color: var(--text-dark);
            font-weight: 500;
        }

        .password-input-container {
            position: relative;
        }

        .form-group input {
            width: 100%;
            padding: 1rem;
            padding-left: 3rem;
            padding-right: 3rem;
            border: 2px solid #e0e0e0;
            border-radius: 10px;
            font-size: 1rem;
            transition: all 0.3s;
            outline: none;
        }

        .form-group input:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
        }

        .form-group input.error {
            border-color: var(--error);
        }

        .input-icon {
            position: absolute;
            left: 1rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--text-light);
        }

        .password-toggle {
            position: absolute;
            right: 1rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: var(--text-light);
            cursor: pointer;
            padding: 0.5rem;
        }

        .form-error {
            color: var(--error);
            font-size: 0.8rem;
            margin-top: 0.3rem;
            display: block;
        }

        .error-message {
            background: rgba(231, 76, 60, 0.1);
            color: var(--error);
            padding: 1rem;
            border-radius: 8px;
            margin-bottom: 1.5rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            text-align: left;
        }

        .error-actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 2rem;
            flex-wrap: wrap;
        }

        .back-to-login {
            margin-top: 2rem;
            padding-top: 2rem;
            border-top: 1px solid #e0e0e0;
        }

        .back-to-login a {
            color: var(--primary-color);
            text-decoration: none;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            justify-content: center;
        }

        .back-to-login a:hover {
            text-decoration: underline;
        }

        .spinner {
            width: 1rem;
            height: 1rem;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-top: 2px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-right: 0.5rem;
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
            .reset-password-content {
                padding: 2rem;
            }
            
            .error-actions {
                flex-direction: column;
            }
        }
    `}</style>
);

export default ResetPassword;